import { isSubmitted } from '@/entities/inspection';
import { enginePackageFor } from '@/entities/template';
import { donePreparing, markPreparing, type TenantDatabase } from '@/shared/offline';

/** Zo lang wacht "Keuring starten" op de opname voordat hij toch het opnamescherm opent. */
export const PREPARE_TIMEOUT_MS = 30_000;

/** Daarna gaat het ophalen op de achtergrond door, met het opnamescherm al open. */
const BACKGROUND_LIMIT_MS = 3 * 60_000;

const POLL_MS = 250;

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Staat deze opname op dit apparaat, met de vragenlijst die ze nodig heeft? Dezelfde vraag
 * als het opnamescherm stelt voordat het "niet op dit apparaat" of "wordt opgehaald" zegt.
 *
 * `editable` vraagt ook of ze weer te bewerken is: na heropenen kan hier nog de ingediende
 * versie staan, tot de synchronisatie de heropende binnenbrengt.
 */
export async function surveyOnDevice(
    db: TenantDatabase,
    inspectionUuid: string,
    options: { editable?: boolean } = {},
): Promise<boolean> {
    const inspection = await db.inspections.get(inspectionUuid);

    if (!inspection || (options.editable && isSubmitted(inspection))) {
        return false;
    }

    /* Zonder vragenlijst valt er niets meer op te halen; het scherm zegt dat zelf. */
    if (inspection.templateVersionUuid === null) {
        return true;
    }

    const [version, document] = await Promise.all([
        db.templateVersions.get(inspection.templateVersionUuid),
        db.packageDocuments.get(inspection.templateVersionUuid),
    ]);

    return enginePackageFor(version, document).status !== 'missing';
}

export interface PrepareSurveyOptions {
    db: TenantDatabase;
    inspectionUuid: string;
    /** Eén synchronisatieronde (`useSyncState().flushNow`). */
    sync: () => Promise<unknown>;
    /** Net heropend: wacht tot de opname hier weer te bewerken is, niet alleen tot ze er staat. */
    editable?: boolean;
    timeoutMs?: number;
    backgroundLimitMs?: number;
}

/**
 * Haal een net gestarte of net heropende opname naar dit apparaat en wacht tot ze er met haar
 * vragenlijst staat.
 *
 * `true` zodra ze er is, `false` na `timeoutMs`. Het ophalen stopt dan niet: het loopt op de
 * achtergrond door tot `backgroundLimitMs`, en zolang noemt `useIsPreparing` de opname,
 * zodat het opnamescherm "De opname wordt klaargezet" zegt en vanzelf opent als ze binnenkomt.
 *
 * Eén ronde tegelijk: is een ronde klaar en de opname er nog niet, dan start meteen de volgende
 * in plaats van te wachten op het gewone ritme van de synchronisatie.
 */
export function prepareSurvey({
    db,
    inspectionUuid,
    sync,
    editable = false,
    timeoutMs = PREPARE_TIMEOUT_MS,
    backgroundLimitMs = BACKGROUND_LIMIT_MS,
}: PrepareSurveyOptions): Promise<boolean> {
    markPreparing(inspectionUuid);

    const arrived = waitUntilOnDevice(db, inspectionUuid, sync, backgroundLimitMs, editable)
        .catch(() => false)
        .finally(() => donePreparing(inspectionUuid));

    return Promise.race([arrived, delay(timeoutMs).then(() => false)]);
}

async function waitUntilOnDevice(
    db: TenantDatabase,
    inspectionUuid: string,
    sync: () => Promise<unknown>,
    limitMs: number,
    editable: boolean,
): Promise<boolean> {
    const deadline = Date.now() + limitMs;
    let round: Promise<unknown> | null = null;

    for (;;) {
        if (await surveyOnDevice(db, inspectionUuid, { editable })) {
            return true;
        }

        if (Date.now() >= deadline) {
            return false;
        }

        round ??= sync()
            .catch(() => undefined)
            .finally(() => {
                round = null;
            });

        await delay(POLL_MS);
    }
}
